import { useCallback, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faUpload, faEye, faTrash, faLock } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import {
  authFetch,
  formatDate,
  openAuthedFile,
  DOCUMENT_TYPE_LABELS,
  DOCUMENT_DISPLAY_ORDER,
  DOCUMENT_STATUS_BADGE,
  DOCUMENT_STATUS_LABELS,
} from '../../utils/http';

interface Doc {
  id: string;
  documentType: number;
  fileName: string;
  fileSizeBytes: number;
  status: number; // 0 Pending, 1 Verified, 2 Rejected
  reviewNote: string | null;
  uploadedAt: string;
}

const DESCRIPTIONS = [
  'A certified copy of your South African ID.',
  'Your certified National Senior Certificate (matric), showing Mathematics.',
  'A commissioned affidavit confirming you are not participating in any SETA programme.',
  'Your current curriculum vitae.',
];

/** Learner's own document uploads. Only the learner and admins can see these. */
export default function StudentDocumentsPanel() {
  const { accessToken } = useAuth();
  const [docs, setDocs] = useState<Doc[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [busyType, setBusyType] = useState<number | null>(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    const res = await authFetch<Doc[]>(accessToken, '/api/student-documents/mine');
    if (res.ok && res.data) setDocs(res.data);
    else setError(res.message || 'Failed to load your documents.');
    setIsLoading(false);
  }, [accessToken]);

  useEffect(() => {
    load();
  }, [load]);

  const upload = async (type: number, file: File) => {
    setError('');
    setBusyType(type);
    const form = new FormData();
    form.append('file', file);
    const res = await authFetch(accessToken, `/api/student-documents/mine/${type}`, { method: 'POST', form });
    setBusyType(null);
    if (!res.ok) {
      setError(res.message);
      return;
    }
    load();
  };

  const view = async (id: string) => {
    const err = await openAuthedFile(accessToken, `/api/student-documents/${id}/file`);
    if (err) setError(err);
  };

  const remove = async (id: string) => {
    if (!window.confirm('Remove this document?')) return;
    const res = await authFetch(accessToken, `/api/student-documents/mine/${id}`, { method: 'DELETE' });
    if (!res.ok) setError(res.message);
    else load();
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <FontAwesomeIcon icon={faSpinner} className="h-8 w-8 text-brand-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700 flex items-start gap-2">
        <FontAwesomeIcon icon={faLock} className="h-3.5 w-3.5 mt-0.5" />
        <span>These documents are private. Only you and the administrators can view them. Your trainers cannot see them.</span>
      </div>

      {error && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">{error}</p>}

      {DOCUMENT_DISPLAY_ORDER.map((type) => {
        const label = DOCUMENT_TYPE_LABELS[type];
        const doc = docs.find((d) => d.documentType === type);
        const isBusy = busyType === type;
        const locked = doc?.status === 1;
        return (
          <div key={type} className="border border-canvas-300 rounded-2xl p-5">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <h3 className="font-bold text-brand-950">{label}</h3>
                <p className="text-xs text-brand-900/50 mt-0.5">{DESCRIPTIONS[type]}</p>
              </div>
              {doc ? (
                <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${DOCUMENT_STATUS_BADGE[doc.status]}`}>
                  {DOCUMENT_STATUS_LABELS[doc.status]}
                </span>
              ) : (
                <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">Not uploaded</span>
              )}
            </div>

            {doc && (
              <p className="text-xs text-brand-900/60 mt-3">
                {doc.fileName} · {Math.max(1, Math.round(doc.fileSizeBytes / 1024))} KB · uploaded {formatDate(doc.uploadedAt)}
              </p>
            )}
            {doc?.status === 2 && doc.reviewNote && (
              <p className="text-xs text-red-700 mt-2 bg-red-50 border border-red-200 rounded-lg p-3">
                <span className="font-semibold">Rejected:</span> {doc.reviewNote}. Please upload a corrected copy.
              </p>
            )}

            <div className="flex flex-wrap gap-2 mt-4">
              {doc && (
                <button
                  onClick={() => view(doc.id)}
                  className="px-3 py-1.5 border border-canvas-300 text-brand-900 text-xs font-semibold rounded-lg hover:bg-canvas-100 transition-colors flex items-center gap-1.5"
                >
                  <FontAwesomeIcon icon={faEye} className="h-3 w-3" />
                  View
                </button>
              )}
              {!locked && (
                <label className="px-3 py-1.5 bg-brand-gradient text-white text-xs font-semibold rounded-lg hover:shadow-lg transition-all cursor-pointer flex items-center gap-1.5">
                  <FontAwesomeIcon icon={isBusy ? faSpinner : faUpload} className={`h-3 w-3 ${isBusy ? 'animate-spin' : ''}`} />
                  {doc ? 'Replace' : 'Upload'}
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    className="hidden"
                    disabled={isBusy}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      e.target.value = '';
                      if (file) upload(type, file);
                    }}
                  />
                </label>
              )}
              {doc && !locked && (
                <button onClick={() => remove(doc.id)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg" title="Remove">
                  <FontAwesomeIcon icon={faTrash} className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>
        );
      })}

      <p className="text-xs text-brand-900/50">PDF, JPG or PNG, up to 10 MB each.</p>
    </div>
  );
}
